import { Button, Divider, Modal } from "antd";
import clsx from "clsx";
import { DocumentStatus } from "constants/documentTemplate";
import { KHCN_LOGO } from "constants/images";
import { renderDocumentLookupLink } from "helper/qrCode";
import { useToast } from "hooks/useToast";
import ContentCopyIcon from "mdi-react/ContentCopyIcon";
import TrayArrowDownIcon from "mdi-react/TrayArrowDownIcon";
import { domToPng } from "modern-screenshot";
import { useCallback, useMemo, useRef } from "react";
import QRCode from "react-qr-code";
import { BaseModalProps } from "types/component";
import { copyContent } from "utils/string";

interface QRCodeModalProps extends BaseModalProps {
  isIssue: boolean;
  registerCode: string;
}

const QRCodeModal = (props: QRCodeModalProps) => {
  const { isOpen, registerCode, isIssue, toggleModal } = props;

  const { toggleToast } = useToast();

  const imageDownloadRef = useRef<HTMLDivElement>(null);

  const renderSVGValue = useMemo(() => {
    if (!registerCode) return "";
    return renderDocumentLookupLink(registerCode.replace("/", ""));
  }, [registerCode]);

  const onDownload = useCallback(() => {
    if (imageDownloadRef.current === null || !renderSVGValue) {
      return;
    }

    domToPng(imageDownloadRef.current)
      .then((dataUrl) => {
        const link = document.createElement("a");
        link.download = `Hình ảnh QR chứng nhận - Số đăng ký ${registerCode}`;
        link.href = dataUrl;
        link.click();
        toggleToast({
          type: "success",
          title: "Đã tải xuống mã QR chứng nhận",
        });
      })
      .catch((err) => {
        console.log(err);
      });
  }, [registerCode, renderSVGValue, toggleToast]);

  const onCopy = () => {
    if (renderSVGValue) {
      toggleToast({
        type: "success",
        title: "Đã sao chép link tra cứu thông tin chứng nhận",
      });
      copyContent(renderSVGValue);
    }
  };

  return (
    <Modal
      centered
      open={isOpen}
      onCancel={() => {
        toggleModal();
      }}
      footer={null}
      className="rounded-xl bg-white max-w-[95vw] md:max-w-[80vw] lg:max-w-[50vw]"
      width={500}
      styles={{
        content: {
          padding: "1rem",
        },
      }}
    >
      <div className="flex flex-col items-center">
        <div ref={imageDownloadRef} className="flex flex-col items-center w-full bg-white rounded-xl pt-4">
          <div className="flex items-center justify-center mb-2">
            <img src={KHCN_LOGO} alt="" width={25} height={25} className="mx-1" />
            <div>
              <h1 className="text-[9px] font-black text-nowrap">SỞ KHOA HỌC VÀ CÔNG NGHỆ</h1>
              <h1 className="text-[9px] font-black text-nowrap">THÀNH PHỐ HỒ CHÍ MINH</h1>
            </div>
          </div>
          <h5 className="text-primary font-black text-sm tex-center flex flex-col items-center justify-center">
            <span className="text-nowrap">XÁC THỰC CHỨNG NHẬN</span>
            <span className="text-nowrap">ỨNG DỤNG CÔNG NGHỆ BLOCKCHAIN</span>
          </h5>
          <Divider className="bg-[##F5F6F8] my-3" />
          <div className="mb-4">
            <div className="font-bold text-md lg:text-lg text-nowrap flex items-center justify-center">
              {`SỐ ĐĂNG KÝ: ${registerCode}`}
              <div
                className={clsx("rounded-2xl px-3 py-1 ml-2 text-xs font-bold text-nowrap", {
                  "bg-neutral-success text-success": isIssue,
                  "bg-neutral-danger text-danger": !isIssue,
                })}
              >
                {!isIssue ? DocumentStatus.Expired : DocumentStatus.Issued}
              </div>
            </div>
          </div>
          <p className="text-sm font-medium mb-2">Quét mã QR để xem chứng nhận</p>
          <div className="flex items-center justify-center mb-5">
            <QRCode level="L" value={renderSVGValue} size={200} />
          </div>
        </div>
        <div className="flex items-center justify-center">
          <Button
            disabled={!renderSVGValue}
            onClick={onCopy}
            className="btn btn-outline btn-outline-primary mx-2 min-w-[130px]"
          >
            <ContentCopyIcon className="text-dark-100 inline-block mr-1" size={16} />
            Sao chép link
          </Button>
          <Button
            disabled={!renderSVGValue}
            onClick={onDownload}
            className="btn btn-outline btn-outline-primary mx-2 min-w-[130px]"
          >
            <TrayArrowDownIcon className="text-dark-100 inline-block mr-1" size={16} />
            Tải xuống
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default QRCodeModal;
